import React from 'react';
import cn from '@/utils/cn';
import {
  dataCellClass, dataEmptyClass, dataFrameClass, dataRowClass,
} from './SPADataTable';

interface SPARowListProps {
  /** One element per row; each is wrapped in a list item styled like a table row. */
  children?: React.ReactNode;
  className?: string;
  /** Shown inside the frame when there are no rows. */
  emptyMessage?: React.ReactNode;
  /** Pinned under the rows, inside the frame — a count line, a "Show all" control. */
  footer?: React.ReactNode;
  striped?: boolean;
}

// AIDEV-NOTE: A one-column SPADataTable for content that is a list, not a grid (reason lists,
// change logs): same raised frame, row heights, dividers, hover, and stripes, taken from the
// table's exported class helpers so the two never drift.
const SPARowList = ({
  children, className, emptyMessage, footer, striped = false,
}: SPARowListProps) => {
  const rows = React.Children.toArray(children);

  return (
    <div className={cn(dataFrameClass, className)}>
      {rows.length === 0 ? (
        <p className={cn('tw-m-0 tw-px-4 tw-text-sm', dataEmptyClass)}>{emptyMessage}</p>
      ) : (
        <ul className="tw-m-0 tw-list-none tw-p-0">
          {rows.map((row, index) => (
            <li
              key={React.isValidElement(row) && row.key != null ? row.key : index}
              className={cn(dataRowClass(index, striped), dataCellClass(index === rows.length - 1))}
            >
              {row}
            </li>
          ))}
        </ul>
      )}
      {footer && (
        <div
          className={cn(
            'tw-flex tw-items-center tw-gap-2 tw-px-4 tw-py-2 tw-text-[0.8125rem] tw-text-spa-tertiary',
            'tw-border-0 tw-border-t tw-border-solid tw-border-spa-border-secondary',
          )}
        >
          {footer}
        </div>
      )}
    </div>
  );
};

export default SPARowList;
